import React from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, Box, Chip, TextField, Typography } from '@mui/material';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { sa } from '../../superAdminUi';
import { describeSites } from '../userEdit';
import { badgeSx, fieldSx, hintSx, rowSx } from './panelStyles';

const ALL_SCHOOLS = { id: 0, name: 'All schools' };

// One card per organisation the user can reach. The organisation this page belongs to is marked
// and — for a trust-level grant on the trust's own page — carries the school picker in place.
// Every other organisation gets an Open link that takes the operator there with this panel open.
function AccessList({
  organisations,
  current,
  scope,
  namesFor,
  schoolNamesById,
  sitesEditable,
  siteOptions,
  sites,
  onSitesChange,
  onOpen,
  disabled,
}) {
  if (organisations.length === 0) {
    return <Typography sx={hintSx}>No school or trust access yet.</Typography>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {organisations.map((org) => {
        const isCurrent = org === current;
        const isGroup = org.kind === 'group';
        const Icon = isGroup ? AccountBalanceOutlinedIcon : SchoolOutlinedIcon;
        const editingSites = isCurrent && isGroup && sitesEditable;
        const detail = isGroup
          ? org.allSchools
            ? 'Trust-wide · all schools'
            : `Trust · ${describeSites(org.siteIds, schoolNamesById)}`
          : 'School';
        const currentLabel = isCurrent
          ? scope === 'group'
            ? isGroup ? 'This trust' : 'In this trust'
            : isGroup ? 'Via this school’s trust' : 'This school'
          : null;

        return (
          <Box
            key={`${org.kind}-${org.id}`}
            sx={{ ...rowSx, flexDirection: 'column', gap: 1, ...(isCurrent && { borderColor: sa.color.teal }) }}
            data-org={`${org.kind}-${org.id}`}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, width: '100%' }}>
              <Icon sx={{ fontSize: 20, mt: '1px', color: isCurrent ? sa.color.teal : sa.color.textGray }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontFamily: sa.font.family, fontSize: 14, fontWeight: sa.font.medium, color: sa.color.text }}>
                  {namesFor(org)}
                </Typography>
                {!editingSites && <Typography sx={hintSx}>{detail}</Typography>}
              </Box>
              {currentLabel ? (
                <Box component='span' sx={badgeSx({ bg: '#e0f2f1', fg: sa.color.tealDark })}>{currentLabel}</Box>
              ) : (
                onOpen && (
                  <Box
                    component='button'
                    type='button'
                    onClick={() => onOpen(org)}
                    disabled={disabled}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      border: 0,
                      background: 'none',
                      p: 0,
                      cursor: 'pointer',
                      fontFamily: sa.font.family,
                      fontSize: 13,
                      fontWeight: sa.font.medium,
                      color: sa.color.teal,
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    Open <ArrowForwardIcon sx={{ fontSize: 15 }} />
                  </Box>
                )
              )}
            </Box>

            {editingSites && (
              <Box sx={{ width: '100%' }}>
                <Autocomplete
                  multiple
                  options={[ALL_SCHOOLS, ...(siteOptions || [])]}
                  value={sites}
                  disabled={disabled}
                  getOptionLabel={(opt) => opt.name}
                  isOptionEqualToValue={(opt, val) => opt.id === val.id}
                  onChange={(_e, next) => {
                    // Choosing "All schools" clears specific picks, and vice-versa.
                    const pickedAll = next.some((s) => s.id === 0) && !sites.some((s) => s.id === 0);
                    onSitesChange(pickedAll ? [] : next.filter((s) => s.id !== 0));
                  }}
                  renderTags={(tagValue, getTagProps) =>
                    tagValue.map((opt, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={opt.id}
                        label={opt.name}
                        size='small'
                        sx={{ fontFamily: sa.font.family, bgcolor: sa.color.schoolBg, color: sa.color.text }}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size='small'
                      placeholder={sites.length ? '' : 'All schools'}
                      inputProps={{ ...params.inputProps, 'aria-label': 'Schools' }}
                      sx={fieldSx}
                    />
                  )}
                />
                <Typography sx={{ ...hintSx, mt: 0.5 }}>
                  Leave empty for every school in the trust, including schools added later.
                </Typography>
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

AccessList.propTypes = {
  organisations: PropTypes.arrayOf(PropTypes.object).isRequired,
  current: PropTypes.object,
  scope: PropTypes.oneOf(['school', 'group']).isRequired,
  namesFor: PropTypes.func.isRequired,
  schoolNamesById: PropTypes.object,
  sitesEditable: PropTypes.bool,
  siteOptions: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
  sites: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
  onSitesChange: PropTypes.func,
  onOpen: PropTypes.func,
  disabled: PropTypes.bool,
};

AccessList.defaultProps = {
  schoolNamesById: {},
  siteOptions: [],
  sites: [],
};

export default AccessList;
